/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { JsonLd } from 'react-schemaorg';
import {
    type FAQPage as FAQPageSchema,
    type Product as ProductSchema,
    type VideoObject as VideoObjectSchema,
    type WithContext,
} from 'schema-dts';

import { type Reviews } from '../../../shared/reviews';
import { type Price, type Product, type Video } from '../../../shared/types';
import { toIsoDuration } from '../../helper/toIsoDuration';
import { CaptionTitle } from '../AccordionContainer/AccordionIcon';

type ProductMarkupProps = {
    readonly markupData: Product & Reviews;
    readonly pageUrl: string;
    readonly price: Price;
};

function hasVideoDuration(video: Video): video is Video & { durationInSeconds: number } {
    return typeof video.durationInSeconds === 'number' && video.durationInSeconds > 0;
}

const ProductMarkup = ({ markupData, pageUrl, price }: ProductMarkupProps): JSX.Element => {
    const canAddRatingToMarkup = Boolean(markupData.rating?.value && markupData.rating?.count);
    const faqGroup = markupData.contentGroups.find(group => group.caption === CaptionTitle.Faq);
    const hasFAQ = Boolean(faqGroup && faqGroup.contentBlocks.length > 0);
    const eligibleVideos = markupData.videos.filter(hasVideoDuration);

    const markup: WithContext<ProductSchema> = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: markupData.title,
        sku: markupData.manufacturerSku,
        mpn: markupData.manufacturerSku,
        brand: {
            '@type': 'Brand',
            name: markupData.brand,
        },
        image: markupData.images.map(image => image.src),
        description: markupData.productInformation.description.body,
        url: pageUrl,
        category: 'LeisureTimeActivity',
        ...(canAddRatingToMarkup && {
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: markupData.rating!.value,
                reviewCount: markupData.rating!.count,
                worstRating: 0,
                bestRating: 5,
            },
        }),
        offers: {
            '@type': 'Offer',
            priceCurrency: price.gross.currencyCode,
            price: price.gross.amount,
            priceValidUntil: price.priceValidUntil,
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            seller: {
                '@type': 'Organization',
                name: 'Jochen-Schweizer GmbH',
            },
            url: pageUrl,
        },
        ...(markupData.reviews && {
            review: markupData.reviews.map(({ author, content, partner, rating, timestamps }) => ({
                '@type': 'UserReview',
                author: {
                    '@type': 'Person',
                    name: author.firstName,
                },
                datePublished: timestamps.created,
                reviewBody: content,
                ...((partner.venue || partner.zipCode) && {
                    contentLocation: {
                        '@type': 'Place',
                        address: {
                            '@type': 'PostalAddress',
                            addressLocality: partner.venue as string,
                            postalCode: partner.zipCode as string,
                        },
                    },
                }),
                ...(rating && {
                    reviewRating: {
                        '@type': 'Rating',
                        bestRating: 5,
                        ratingValue: rating,
                        worstRating: 0,
                    },
                }),
            })),
        }),
    };

    const faqMarkup: WithContext<FAQPageSchema> | null =
        hasFAQ && faqGroup
            ? {
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: faqGroup.contentBlocks
                      .filter(block => block.caption && block.body)
                      .map(block => ({
                          '@type': 'Question',
                          name: block.caption as string,
                          acceptedAnswer: {
                              '@type': 'Answer',
                              text: block.body,
                          },
                      })),
              }
            : null;

    const videoMarkups = eligibleVideos.reduce<Array<WithContext<VideoObjectSchema>>>(
        (markups, video) => {
            const sourceUrl = video.src;
            const thumbnailUrl = video.poster.src || video.thumbnail;

            markups.push({
                '@context': 'https://schema.org',
                '@type': 'VideoObject',
                ...(video.title && { name: video.title }),
                ...(video.description && { description: video.description }),
                ...(thumbnailUrl && { thumbnailUrl: [thumbnailUrl] }),
                duration: toIsoDuration(video.durationInSeconds),
                contentUrl: sourceUrl,
                embedUrl: pageUrl,
                uploadDate: video.uploadDate?.dateTime,
                isFamilyFriendly: true,
                inLanguage: 'de',
                publisher: {
                    '@type': 'Organization',
                    name: 'Jochen-Schweizer GmbH',
                },
                potentialAction: {
                    '@type': 'WatchAction',
                    target: sourceUrl,
                },
            });

            return markups;
        },
        [],
    );

    return (
        <>
            <JsonLd<ProductSchema> item={markup} />
            {faqMarkup && <JsonLd<FAQPageSchema> item={faqMarkup} />}
            {eligibleVideos &&
                videoMarkups.map(videoMarkup => (
                    <JsonLd<VideoObjectSchema>
                        key={videoMarkup.contentUrl as string}
                        item={videoMarkup}
                    />
                ))}
        </>
    );
};

export { ProductMarkup };
